/**
 * Library (PRD-1605): one collection of documents in three types. A document
 * has a name, a type, a file and an upload state; nothing is parsed, so
 * Available is terminal — the file is stored.
 *
 * There is no Library API yet, so the model holds the documents itself,
 * seeded locally. It is created once in App.js and lives for the session,
 * which is what lets the page restore the tab, filters, view and search a
 * user left when they come back to it.
 */

export const LIBRARY_TYPES = {
  reference_key: {
    label: 'Reference key',
    plural: 'Reference keys',
    one: 'reference key',
    many: 'reference keys',
    icon: 'grid-four',
    hint: 'The command rubric the output is marked against.',
  },
  sop: {
    label: 'SOP',
    plural: 'SOPs',
    one: 'SOP',
    many: 'SOPs',
    icon: 'book-open',
    hint: "The instructor's run book. Context only, not scored.",
  },
  narrative: {
    label: 'Narrative',
    plural: 'Narratives',
    one: 'narrative',
    many: 'narratives',
    icon: 'file-text',
    hint: 'The scenario story given to the crew.',
  },
};

export const LIBRARY_TYPE_ORDER = ['reference_key', 'sop', 'narrative'];

export const DEFAULT_LIBRARY_FILTERS = {
  visibility: 'active',
  usage: 'all',
  sort: 'date_desc',
  search: '',
};

/* PRD-1700: the toolbar's Sort drives the grid; in the list the headers sort,
   and the two stay in step where they overlap (name and upload date). */
const SORT_TO_COLUMN = {
  name_asc: { key: 'name', dir: 'asc' },
  name_desc: { key: 'name', dir: 'desc' },
  date_asc: { key: 'uploaded', dir: 'asc' },
  date_desc: { key: 'uploaded', dir: 'desc' },
};

/** The list's header sort that a toolbar Sort value stands for. */
export function columnSortFor(sort) {
  return SORT_TO_COLUMN[sort] || SORT_TO_COLUMN[DEFAULT_LIBRARY_FILTERS.sort];
}

/** The toolbar Sort value a header sort stands for, or `current` when it has none. */
export function toolbarSortFor(columnSort, current) {
  const hit = Object.keys(SORT_TO_COLUMN)
    .find((k) => SORT_TO_COLUMN[k].key === columnSort.key && SORT_TO_COLUMN[k].dir === columnSort.dir);
  return hit || current;
}

const HOUR = 3600000;
const DAY = 86400000;

/**
 * Dated from load so every rung of the relative-date ladder shows: minutes,
 * hours, yesterday, a weekday, and one beyond a week. One archived and one
 * failed, so both states are reachable without waiting. Names are invented.
 */
export function seedLibraryDocuments(now = Date.now()) {
  const ago = (ms) => new Date(now - ms).toISOString();
  return [
    { id: 'd1', type: 'reference_key', name: 'Bridge Command Reference Key', fileName: 'Bridge_Command_Reference_Key_v3.xlsx', uploadedAt: ago(9 * DAY), status: 'available', usedBy: 0 },
    { id: 'd2', type: 'reference_key', name: 'Harbour Pilotage Reference Key', fileName: 'Harbour_Pilotage_Reference_Key.xlsx', uploadedAt: ago(4 * DAY), status: 'available', usedBy: 0 },
    { id: 'd3', type: 'reference_key', name: 'Towage Reference Key', fileName: 'Towage_Reference_Key_draft.csv', uploadedAt: ago(2 * HOUR), status: 'failed', usedBy: 0 },
    { id: 'd4', type: 'sop', name: 'Simulator Run Book', fileName: 'Bridge_Simulator_SOP.pdf', uploadedAt: ago(9 * DAY), status: 'available', usedBy: 0 },
    { id: 'd5', type: 'sop', name: 'Night Order Book', fileName: 'Night_Order_Book_2026.pdf', uploadedAt: ago(DAY + 3 * HOUR), status: 'available', usedBy: 0 },
    { id: 'd6', type: 'sop', name: 'Legacy Watchkeeping SOP', fileName: 'Watchkeeping_SOP_2019.pdf', uploadedAt: ago(26 * DAY), status: 'available', archived: true, usedBy: 0 },
    { id: 'd7', type: 'narrative', name: 'Fishing Fleet Crossing', fileName: 'Narrative_Fishing_Fleet_Crossing.pdf', uploadedAt: ago(4 * DAY), status: 'available', usedBy: 0 },
    { id: 'd8', type: 'narrative', name: 'Fog Bank on Final Approach', fileName: 'Narrative_Fog_Bank_Approach.pdf', uploadedAt: ago(DAY + 5 * HOUR), status: 'available', usedBy: 0 },
    { id: 'd9', type: 'narrative', name: 'Crew Overboard, Port Quarter', fileName: 'Narrative_Crew_Overboard.pdf', uploadedAt: ago(18 * 60000), status: 'available', usedBy: 0 },
  ];
}

/**
 * How many scenarios point at the document. The model keeps it in step with
 * its scenarios (PRD-1701); with scenarios off, every document is at zero.
 */
export function usedBy(doc) {
  return doc.usedBy || 0;
}

/** "1 scenario" / "2 scenarios": the one place the Library picks singular or plural. */
export function plural(n, one, many) {
  return n === 1 ? one : many;
}

/**
 * Why a document can't be archived, as the toast says it, or null (PRD-1607).
 * A document a scenario still points at refuses rather than guessing at a
 * cascade, because binding lifecycle is an open question in the handoff.
 */
export function archiveProblem(doc) {
  const n = usedBy(doc);
  return n ? `In use by ${n} ${plural(n, 'scenario', 'scenarios')}. Change the scenario first.` : null;
}

function inVisibility(doc, visibility) {
  if (visibility === 'archived') return !!doc.archived;
  if (visibility === 'all') return true;
  return !doc.archived;
}

/* ---- Files (PRD-1606) ----------------------------------------------------
   Nothing is parsed or validated structurally: the extension is checked and the
   size capped, and that is all. */
export const LIBRARY_FILE_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
export const LIBRARY_FILE_ACCEPT = LIBRARY_FILE_EXTENSIONS.map((e) => `.${e}`).join(',');
export const LIBRARY_FILE_LABEL = 'PDF, Word, Excel or CSV';
export const LIBRARY_MAX_FILE_MB = 100;

/** Why a file can't be added, as the toast says it, or null when it can. */
export function libraryFileProblem(file) {
  const ext = (String(file.name).split('.').pop() || '').toLowerCase();
  if (!LIBRARY_FILE_EXTENSIONS.includes(ext)) return `Not a document. Add a ${LIBRARY_FILE_LABEL} file.`;
  if (file.size > LIBRARY_MAX_FILE_MB * 1024 * 1024) return `That file is over ${LIBRARY_MAX_FILE_MB}mb.`;
  return null;
}

/** Sizes read as 412kb and 1.8mb: lower case, no space. */
export function formatFileSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}kb`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}mb`;
}

const EMPTY_DRAFT = { name: '', type: null, file: null };
const UPLOAD_TICK_MS = 700;

/** Documents of the open type that pass the filters and search, sorted. */
export function filterLibraryDocuments(documents, type, filters) {
  const term = filters.search.trim().toLowerCase();
  const out = documents.filter((d) => {
    if (d.type !== type || !inVisibility(d, filters.visibility)) return false;
    if (filters.usage === 'in' && usedBy(d) === 0) return false;
    if (filters.usage === 'out' && usedBy(d) > 0) return false;
    if (!term) return true;
    return d.name.toLowerCase().includes(term) || d.fileName.toLowerCase().includes(term);
  });
  // The same order as the list's Name header (ListTable sortRows).
  const byName = (a, b) => a.name.localeCompare(b.name, 'en', { numeric: true, sensitivity: 'base' });
  if (filters.sort === 'name_asc') return out.sort(byName);
  if (filters.sort === 'name_desc') return out.sort((a, b) => byName(b, a));
  if (filters.sort === 'date_asc') return out.sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt));
  return out.sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
}

/* ---- Scenarios (PRD-1701) ------------------------------------------------
   A scenario is a saved selection of Library documents: a name, at least one
   reference key, and optionally one narrative and any number of SOPs. Nothing
   is parsed. The reference key is what a run is compared against; the other
   two only add context (Navy Phase 0 handoff, "Scenarios"). */

/** The prototype's three, on the seeded documents. */
export function seedLibraryScenarios() {
  return [
    { id: 's1', name: 'Strait Transit - Fishing Season', narrativeId: 'd7', referenceKeyIds: ['d1'], sopIds: ['d4'] },
    { id: 's2', name: 'Restricted Visibility Approach', narrativeId: 'd8', referenceKeyIds: ['d1'], sopIds: ['d4'] },
    { id: 's3', name: 'Man Overboard Recovery', narrativeId: 'd9', referenceKeyIds: ['d1'], sopIds: ['d4'] },
  ];
}

// Scenarios open on All, where the Library opens on Active: an archived
// scenario is still what an assessment was run against.
export const DEFAULT_SCENARIO_FILTERS = { search: '', visibility: 'all' };

/** Every scenario pointing at a document, archived ones included. */
export function scenariosUsing(scenarios, docId) {
  return scenarios.filter((s) => s.referenceKeyIds.includes(docId) || s.sopIds.includes(docId) || s.narrativeId === docId);
}

/** Ready once it has a reference key; anything less reads Incomplete. */
export const scenarioComplete = (scenario) => scenario.referenceKeyIds.length > 0;

/** Active assessments run against the scenario (PRD-1703 pushes the counts in). */
export const scenarioUsage = (scenario) => scenario.usage || 0;

export function documentNames(documents, ids) {
  return ids.map((id) => documents.find((d) => d.id === id)?.name).filter(Boolean);
}

/** One name in full; several as the first plus a count, so a column stays a column. */
export function documentSummary(documents, ids) {
  const names = documentNames(documents, ids);
  if (!names.length) return '-';
  return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
}

/**
 * What a scenario can pick: the type's documents that finished uploading and
 * aren't archived. One already picked stays, so editing never drops it silently.
 */
export function pickableDocuments(documents, type, selected = []) {
  return documents.filter((d) => d.type === type
    && ((d.status === 'available' && !d.archived) || selected.includes(d.id)));
}

/** The search reads what the row shows: the name and the documents behind it. */
export function filterScenarios(scenarios, documents, filters) {
  const term = filters.search.trim().toLowerCase();
  return scenarios.filter((s) => {
    if (!inVisibility(s, filters.visibility)) return false;
    if (!term) return true;
    const text = [s.name, ...documentNames(documents, [...s.referenceKeyIds, ...s.sopIds, ...(s.narrativeId ? [s.narrativeId] : [])])];
    return text.some((t) => t.toLowerCase().includes(term));
  });
}

/** The scenarios count line's "of N": every scenario in the visibility scope. */
export function scenarioScopeTotal(scenarios, visibility) {
  return scenarios.filter((s) => inVisibility(s, visibility)).length;
}

/** The "of N" in the count line: the open type within the visibility scope. */
export function libraryScopeTotal(documents, type, visibility) {
  return documents.filter((d) => d.type === type && inVisibility(d, visibility)).length;
}

export class LibraryModel {
  /**
   * `random` and `tickMs` only exist so tests can drive the upload engine
   * deterministically; the app takes the defaults.
   */
  constructor(documents = seedLibraryDocuments(), { random = Math.random, tickMs = UPLOAD_TICK_MS, scenarios = null } = {}) {
    this.documents = documents;
    // null while scenarios are off (`navy-assessments`): "Used by" stays as the documents say.
    this.scenarios = scenarios;
    this.scenarioFilters = { ...DEFAULT_SCENARIO_FILTERS };
    this.scenarioSort = { key: 'name', dir: 'asc' }; // Scenario, A to Z
    this.usageCounts = {}; // scenario id -> active assessments, from the AssessmentsModel
    this.syncUsage();
    this.type = LIBRARY_TYPE_ORDER[0];
    this.view = 'grid';
    this.filters = { ...DEFAULT_LIBRARY_FILTERS };
    this.listSort = null; // the list's header sort; null follows the toolbar's Sort
    this.draft = { ...EMPTY_DRAFT };
    this.listeners = [];
    this.random = random;
    this.tickMs = tickMs;
    this.uploadTimer = null;
    this.nextId = 1;
  }

  /** A tab changes the type and nothing else: filters, sort, view and search carry across. */
  setType(type) {
    if (!LIBRARY_TYPES[type] || type === this.type) return;
    this.type = type;
    this.notify();
  }

  setView(view) {
    if (view !== 'grid' && view !== 'list') return;
    this.view = view;
    this.notify();
  }

  setFilter(key, value) {
    if (!(key in this.filters)) return;
    this.filters = { ...this.filters, [key]: value };
    if (key === 'sort') this.listSort = null;
    this.notify();
  }

  /** A header sort in the list. The toolbar's Sort follows it where it has the same order. */
  setListSort(sort) {
    this.listSort = sort;
    this.filters = { ...this.filters, sort: toolbarSortFor(sort, this.filters.sort) };
    this.notify();
  }

  /** The no-result way out. The tab, sort and view are what the user chose to look at, so they stay. */
  clearSearchAndFilters() {
    this.filters = {
      ...this.filters,
      search: DEFAULT_LIBRARY_FILTERS.search,
      visibility: DEFAULT_LIBRARY_FILTERS.visibility,
      usage: DEFAULT_LIBRARY_FILTERS.usage,
    };
    this.notify();
  }

  /** A part-filled Add document form survives being dismissed and comes back on the next open. */
  setDraft(patch) {
    this.draft = { ...this.draft, ...patch };
    this.notify();
  }

  /**
   * Saves a document and starts its upload. It lands on its own type's tab,
   * whichever tab was open, and the draft clears. If the search or filters
   * carried over would hide it, they clear too, so the user always sees what
   * they just added climb; the sort and view stay.
   */
  addDocument({ name, type, file }) {
    if (!LIBRARY_TYPES[type] || !name.trim() || !file) return null;
    const doc = {
      id: `n${this.nextId++}`,
      type,
      name: name.trim(),
      fileName: file.name,
      size: file.size,
      uploadedAt: new Date().toISOString(),
      status: 'uploading',
      progress: 0,
      usedBy: 0,
    };
    this.documents = [doc, ...this.documents];
    this.type = type;
    this.draft = { ...EMPTY_DRAFT };
    if (!filterLibraryDocuments([doc], type, this.filters).length) {
      this.filters = {
        ...this.filters,
        search: DEFAULT_LIBRARY_FILTERS.search,
        visibility: DEFAULT_LIBRARY_FILTERS.visibility,
        usage: DEFAULT_LIBRARY_FILTERS.usage,
      };
    }
    this.notify();
    this.startUploads();
    return doc;
  }

  /** Restarts a failed transfer from zero. There is no resume (handoff open question 3). */
  retry(id) {
    const doc = this.documents.find((d) => d.id === id);
    if (!doc || doc.status !== 'failed') return;
    this.updateDocument(id, { status: 'uploading', progress: 0 });
    this.startUploads();
  }

  /**
   * Objects are archived, never deleted (PRD-1607). An archived document keeps
   * its last upload state and is reachable under the Archived filter. Returns
   * whether it archived; a document in use by a scenario refuses, and so does
   * one still uploading (Uploading has no actions in the handoff).
   */
  archive(id) {
    const doc = this.documents.find((d) => d.id === id);
    if (!doc || doc.archived || doc.status === 'uploading' || archiveProblem(doc)) return false;
    this.updateDocument(id, { archived: true });
    return true;
  }

  unarchive(id) {
    const doc = this.documents.find((d) => d.id === id);
    if (!doc || !doc.archived) return false;
    this.updateDocument(id, { archived: false });
    return true;
  }

  /** Search and visibility on Settings > Assessments. */
  setScenarioFilter(key, value) {
    if (!(key in this.scenarioFilters)) return;
    this.scenarioFilters = { ...this.scenarioFilters, [key]: value };
    this.notify();
  }

  setScenarioSort(sort) {
    this.scenarioSort = sort;
    this.notify();
  }

  /** The way out of no results: search and visibility go back to how the tab opened; the sort stays. */
  clearScenarioFilters() {
    this.scenarioFilters = { ...DEFAULT_SCENARIO_FILTERS };
    this.notify();
  }

  /**
   * Adds a scenario, or updates one when `id` is given. It needs a name and a
   * reference key; blank and repeated picks are dropped rather than saved. If
   * the search or visibility would hide it, they clear, so the user sees what
   * they just saved (as addDocument does). Returns the saved scenario, or null
   * when it can't save.
   */
  saveScenario({ id, name, narrativeId, referenceKeyIds, sopIds }) {
    if (!this.scenarios || (id && !this.scenarios.some((s) => s.id === id))) return null;
    const dedupe = (ids) => ids.filter((v, i) => v && ids.indexOf(v) === i);
    const fields = {
      name: name.trim(),
      narrativeId: narrativeId || null,
      referenceKeyIds: dedupe(referenceKeyIds),
      sopIds: dedupe(sopIds),
    };
    if (!fields.name || !fields.referenceKeyIds.length) return null;
    let saved;
    if (id) {
      this.scenarios = this.scenarios.map((s) => (s.id === id ? (saved = { ...s, ...fields }) : s));
    } else {
      saved = { id: `sc${this.nextId++}`, ...fields };
      this.scenarios = [...this.scenarios, saved];
    }
    if (!filterScenarios([saved], this.documents, this.scenarioFilters).length) {
      this.scenarioFilters = { ...DEFAULT_SCENARIO_FILTERS };
    }
    this.syncUsage();
    this.notify();
    return saved;
  }

  /**
   * Turns scenarios on or off without replacing the model, so the Library
   * keeps its documents, uploads and filters when `navy-assessments` changes.
   * Off parks the scenarios and every document reads as not in use; back on,
   * the parked ones return, so the seed is only ever the first set.
   */
  setScenariosOn(on) {
    if (on === !!this.scenarios) return;
    if (on) {
      this.scenarios = (this.parkedScenarios || seedLibraryScenarios())
        .map((sc) => ({ ...sc, usage: this.usageCounts[sc.id] || 0 }));
      this.syncUsage();
    } else {
      this.parkedScenarios = this.scenarios;
      this.scenarios = null;
      this.documents = this.documents.map((d) => (d.usedBy ? { ...d, usedBy: 0 } : d));
    }
    this.notify();
  }

  /** Each scenario's Usage: the active assessments run against it (PRD-1703). */
  setScenarioUsage(counts) {
    this.usageCounts = counts;
    if (!this.scenarios) return;
    this.scenarios = this.scenarios.map((s) => {
      const usage = counts[s.id] || 0;
      return usage === scenarioUsage(s) ? s : { ...s, usage };
    });
    this.notify();
  }

  /** Archived, never deleted. One an assessment still uses refuses. Returns whether it archived. */
  archiveScenario(id) {
    const scenario = this.scenarios?.find((s) => s.id === id);
    if (!scenario || scenario.archived || scenarioUsage(scenario)) return false;
    this.setScenarioArchived(id, true);
    return true;
  }

  unarchiveScenario(id) {
    const scenario = this.scenarios?.find((s) => s.id === id);
    if (!scenario || !scenario.archived) return false;
    this.setScenarioArchived(id, false);
    return true;
  }

  setScenarioArchived(id, archived) {
    this.scenarios = this.scenarios.map((s) => (s.id === id ? { ...s, archived } : s));
    this.notify();
  }

  /** Each document's "Used by" is the scenarios pointing at it. */
  syncUsage() {
    if (!this.scenarios) return;
    this.documents = this.documents.map((d) => {
      const n = scenariosUsing(this.scenarios, d.id).length;
      return n === (d.usedBy || 0) ? d : { ...d, usedBy: n };
    });
  }

  /**
   * One interval for the whole page, stopped as soon as nothing is uploading.
   * The rate is a demo convenience, not a model of real transfer.
   */
  startUploads() {
    if (this.uploadTimer) return;
    this.uploadTimer = setInterval(() => this.tickUploads(), this.tickMs);
  }

  tickUploads() {
    if (!this.documents.some((d) => d.status === 'uploading' && !d.archived)) {
      this.stopUploads();
      return;
    }
    this.documents = this.documents.map((d) => {
      if (d.status !== 'uploading' || d.archived) return d;
      const progress = Math.min(100, (d.progress || 0) + 4 + this.random() * 6);
      return progress >= 100 ? { ...d, status: 'available', progress: 100 } : { ...d, progress };
    });
    this.notify();
  }

  stopUploads() {
    clearInterval(this.uploadTimer);
    this.uploadTimer = null;
  }

  updateDocument(id, patch) {
    this.documents = this.documents.map((d) => (d.id === id ? { ...d, ...patch } : d));
    this.notify();
  }

  getState() {
    return {
      documents: this.documents,
      type: this.type,
      view: this.view,
      filters: this.filters,
      listSort: this.listSort || columnSortFor(this.filters.sort),
      draft: this.draft,
      scenarios: this.scenarios,
      scenarioFilters: this.scenarioFilters,
      scenarioSort: this.scenarioSort,
    };
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  notify() {
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
  }
}
